import type { INotification } from "@edaz/services/baseNotificationService";
import BaseNotificationService from "@edaz/services/baseNotificationService";
import { bound } from "@frui.ts/helpers/dist";
import { observable } from "mobx";
import type EventBus from "./eventBus";
import { notificationAdded, serverError, userLogout } from "./eventBus";
import type LocalizationService from "./localizationService";

export enum NotificationType {
  INFO = "info",
  WARNING = "warning",
  SUCCESS = "success",
  ERROR = "error",
}

export interface NotificationAction {
  func: () => void;
  link?: string;
  title?: string;
}

export interface Notification extends INotification {
  type: NotificationType;
  permanent?: boolean;
  closable?: boolean;
  action?: NotificationAction;
  timeToLive?: number;
}

export default class NotificationService extends BaseNotificationService<Notification> {
  @observable notifications: Map<string, Notification>;

  constructor(private eventBus: EventBus, private localizationService: LocalizationService) {
    super();
    this.eventBus.subscribe(serverError, this.addServerError);
    this.eventBus.subscribe(userLogout, this.clearNotifications);
  }

  @bound
  add(notification: Notification, scope?: string): string {
    const key = super.add(notification, scope);
    if (!notification.permanent) {
      setTimeout(() => this.remove(key), (notification.timeToLive ?? 5) * 1000);
    }
    this.eventBus.publish(notificationAdded({ notification }));

    return key;
  }

  @bound
  protected addServerError(event: ReturnType<typeof serverError>) {
    const payload = event.payload;
    let errorMessage: string;
    if (payload.type) {
      errorMessage = this.localizationService.translateError(payload.type);
    } else if (payload.message) {
      errorMessage = payload.message;
    } else {
      errorMessage = this.localizationService.translateError("communicationServerError");
    }

    this.add({ title: errorMessage, type: NotificationType.ERROR });
  }

  @bound
  addNotAllowedError() {
    this.add({ title: this.localizationService.translateError("REQUEST_FORBIDDEN"), type: NotificationType.ERROR });
  }

  @bound
  addUnexpectedError() {
    this.add({ title: this.localizationService.translateError("unexpectedError"), type: NotificationType.ERROR });
  }

  @bound
  protected clearNotifications() {
    this.notifications.clear();
  }
}
